const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PRELOAD_ROOT_MARGIN = '320px 0px';
const PLAY_THRESHOLD = 0.25;
const equipmentVideoControllers = new WeakMap();

const initialiseEquipmentVideo = (root = document) => root.querySelectorAll('[data-equipment-video]').forEach((container) => {
  if (equipmentVideoControllers.has(container)) return;

  const video = container.querySelector('video');
  const control = container.querySelector('[data-equipment-video-control]');
  if (!video || !control) return;

  container.dataset.equipmentVideoInitialised = 'true';
  const videoLabel = container.dataset.equipmentVideoLabel || 'equipment video';
  const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
  const abortController = new AbortController();
  const { signal } = abortController;
  let isVisible = false;
  let userPaused = false;
  let userRequestedPlay = false;
  let preloadObserver = null;
  let playbackObserver = null;

  const showPoster = () => {
    container.dataset.equipmentVideoState = 'error';
    video.hidden = true;
    control.hidden = true;
  };

  const updateControl = () => {
    const isPaused = video.paused;
    const label = `${isPaused ? 'Play' : 'Pause'} ${videoLabel}`;

    container.dataset.equipmentVideoState = isPaused ? 'paused' : 'playing';
    control.setAttribute('aria-label', label);
    control.title = label;
  };

  const warmVideo = () => {
    if (video.dataset.preloadReady === 'true' || (navigator.connection?.saveData && !userRequestedPlay)) return;

    video.dataset.preloadReady = 'true';
    video.preload = 'auto';
    video.load();
  };

  const syncPlayback = () => {
    const motionAllowsAutoplay = !reducedMotion.matches && !navigator.connection?.saveData && !userPaused;
    const shouldPlay = isVisible && !document.hidden && (motionAllowsAutoplay || userRequestedPlay);

    if (!shouldPlay) {
      video.pause();
      return;
    }

    warmVideo();
    video.play().catch(() => {
      if (video.error) showPoster();
      updateControl();
    });
  };

  control.addEventListener(
    'click',
    () => {
      if (video.paused) {
        userPaused = false;
        userRequestedPlay = true;
        warmVideo();
        video.play().catch(() => {
          if (video.error) showPoster();
          updateControl();
        });
        return;
      }

      userPaused = true;
      userRequestedPlay = false;
      video.pause();
    },
    { signal },
  );

  video.addEventListener('error', showPoster, { signal });
  video.addEventListener(
    'loadeddata',
    () => {
      video.hidden = false;
      control.hidden = false;
      updateControl();
    },
    { signal },
  );
  video.addEventListener('play', updateControl, { signal });
  video.addEventListener('pause', updateControl, { signal });
  document.addEventListener('visibilitychange', syncPlayback, { signal });
  reducedMotion.addEventListener?.('change', syncPlayback, { signal });

  if (video.error) {
    showPoster();
    return;
  }

  updateControl();

  if ('IntersectionObserver' in window) {
    preloadObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        warmVideo();
        preloadObserver.disconnect();
      },
      { rootMargin: PRELOAD_ROOT_MARGIN },
    );
    preloadObserver.observe(container);

    playbackObserver = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting && entry.intersectionRatio >= PLAY_THRESHOLD;
        syncPlayback();
      },
      { threshold: [0, PLAY_THRESHOLD, 1] },
    );
    playbackObserver.observe(container);
  } else {
    isVisible = true;
    warmVideo();
    syncPlayback();
  }

  equipmentVideoControllers.set(container, {
    destroy() {
      video.pause();
      preloadObserver?.disconnect();
      playbackObserver?.disconnect();
      abortController.abort();
      equipmentVideoControllers.delete(container);
    },
  });
});

initialiseEquipmentVideo();
document.addEventListener('shopify:section:load', (event) => initialiseEquipmentVideo(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-equipment-video]').forEach((container) => {
    equipmentVideoControllers.get(container)?.destroy();
  });
});
